import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

import messages from "../messages/ja.json";
import { ErrorDialog } from "./app-dialog";
import {
  FrameOrientation,
  FrameSourceMode,
  WorkerFrameSource,
  drawOrientedFrame,
  isSalesforceIPhoneWebView,
  orientedSize,
  preferredFrameSourceMode,
  startWorkerFrameSource,
} from "./camera-frame-worker";
import { Icon } from "./icon";
import {
  CENTER_POINT_CHECKIN,
  CENTER_POINT_REGISTER,
  HeadPose,
  headPoseFromMatrix,
  isCentered,
} from "./head-pose";
import "../styles/auth-flow.css";
import "../styles/face-registration.css";

export type CaptureUsage = "register" | "checkin";

// A captured frame ready for upload. `blob` is produced by canvas.toBlob(); it is what gets
// POSTed to the backend as the multipart `files` field. `url` is an object URL for the
// on-screen preview — FaceCapture owns its lifetime and revokes it when the image is
// replaced/cleared or on unmount.
export interface CapturedImage {
  blob: Blob;
  url: string;
}

interface Props {
  // Registration (controlled preview flow): the captured frame is held in `value` and the
  // parent renders the capture/submit buttons.
  value?: CapturedImage | null;
  onChange?: (image: CapturedImage | null) => void;
  // Check-in (auto-submit flow): the framed face is captured automatically after ~2s and
  // handed straight here for immediate verification — no review/confirm step.
  onCapture?: (image: CapturedImage) => void;
  // Hard lock: no auto-capture, controls disabled (e.g. missing org, or a terminal state
  // the parent won't retry from). No overlay.
  disabled?: boolean;
  // Check-in only: a verify is in flight — pauses auto-capture and shows the processing
  // overlay over the live camera.
  submitting?: boolean;
  // Fired when the camera cannot be opened (permission denied / hardware error), so the
  // parent can offer the secret-question fallback. The message is already localized.
  onCameraError?: (message: string) => void;
  // Keep the camera failure out of the エラー modal and leave it entirely to onCameraError.
  // Check-in sets this: there the failure is not a dead end but a fork — the user switches
  // to the secret question — and a modal that must be dismissed before the link underneath
  // becomes visible only hides the way out. Registration keeps the modal (default false):
  // it has no fallback route, so the failure really is blocking there.
  suppressCameraErrorDialog?: boolean;
  mode?: CaptureUsage;
  // Registration only: show the "registering" spinner overlay centered over the photo.
  loading?: boolean;
  onUiStateChange?: (state: FaceCaptureUiState) => void;
}

type CameraState = "idle" | "starting" | "ready" | "error";

export interface FaceCaptureHandle {
  capture: () => void;
}

export interface FaceCaptureUiState {
  cameraState: CameraState;
  captureReady: boolean;
  hasImage: boolean;
  modelLoading: boolean;
}

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const LOCAL_WASM_URL = `${PUBLIC_URL}/mediapipe/wasm`;
const LOCAL_MODEL_URL = `${PUBLIC_URL}/mediapipe/face_landmarker.task`;
const CDN_WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";
const CDN_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const MAX_FACES = 2;

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: "user", width: { ideal: 960 }, height: { ideal: 720 } },
  audio: false,
};

// Camera diagnostics (failure dumps, track ended/mute listeners, devicechange, the
// frozen-preview watchdog) are a development aid only: they are noisy, they poll once a
// second, and they print device ids and the user agent. CRA inlines NODE_ENV at build
// time, so every guarded block below is dropped from the production bundle outright.
const DIAGNOSTICS_ENABLED = process.env.NODE_ENV !== "production";

const FACE_MIN_CONFIDENCE = 0.7;
const FACE_GUIDE_MIN_INSIDE_RATIO = 0.7;

const DETECT_INTERVAL_MS = 90;
const STABLE_READS = 3;

// Auto-capture (check-in only): once the capture gate — a single, centered face — has held
// continuously for this long, the frame is captured without a tap. Any gate drop (no face,
// 2+ faces, or the head leaving the center point) clears the pending timer, restarting it.
const AUTO_CAPTURE_MS = 2000;

// Center-point (isCentered) decision for the current pose — MORP-2081. The face is
// centered when pitch and yaw are both within `centerPoint` degrees of straight-on
// (roll is not part of the check). Returns null when there is no single face so the
// gate resets. The STABLE_READS debounce in the loop keeps the hint from flickering
// near the boundary, so no enter/exit hysteresis is needed here.
function nextCentered(pose: HeadPose | null, centerPoint: number): boolean | null {
  if (!pose) return null;
  return isCentered(pose, centerPoint);
}

interface RenderRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Pixel size of the raw camera frame. The <video> satisfies it directly; the worker path
// (camera-frame-worker.ts) reports its canvas size in the same shape.
type FrameSize = Pick<HTMLVideoElement, "videoWidth" | "videoHeight">;

// The current raw (un-mirrored) camera frame, from whichever source is active.
interface Frame {
  image: HTMLVideoElement | HTMLCanvasElement;
  size: FrameSize;
}

function getRenderedVideoRect(
  video: FrameSize,
  previewCanvas: HTMLCanvasElement
): RenderRect | null {
  const videoW = video.videoWidth;
  const videoH = video.videoHeight;
  const boxW = previewCanvas.clientWidth;
  const boxH = previewCanvas.clientHeight;
  if (!videoW || !videoH || !boxW || !boxH) return null;
  const objectFit = getComputedStyle(previewCanvas).objectFit || "fill";
  if (objectFit === "contain" || objectFit === "cover") {
    const scale =
      objectFit === "contain"
        ? Math.min(boxW / videoW, boxH / videoH)
        : Math.max(boxW / videoW, boxH / videoH);
    const width = videoW * scale;
    const height = videoH * scale;
    return { x: (boxW - width) / 2, y: (boxH - height) / 2, width, height };
  }
  return { x: 0, y: 0, width: boxW, height: boxH };
}

export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

// Map the on-screen guide box back to a rectangle in RAW video pixels — the inverse of the
// preview rendering (object-fit letterboxing + the horizontal mirror). Detection then runs
// on nothing but this crop, so a face outside the box is never fed to the model at all.
// Exported for the geometry tests; the mapping is what decides which pixels get processed.
export function getGuideCropInVideo(
  video: FrameSize,
  previewCanvas: HTMLCanvasElement,
  guideBox: HTMLDivElement
): CropRect | null {
  const videoW = video.videoWidth;
  const videoH = video.videoHeight;
  const rendered = getRenderedVideoRect(video, previewCanvas);
  if (!rendered || !videoW || !videoH) return null;
  const canvasRect = previewCanvas.getBoundingClientRect();
  const guideRect = guideBox.getBoundingClientRect();
  if (!canvasRect.width || !canvasRect.height || !guideRect.width || !guideRect.height) {
    return null;
  }

  // Viewport px → normalized position within the *mirrored* rendered frame.
  const originX = canvasRect.left + rendered.x;
  const originY = canvasRect.top + rendered.y;
  const mirroredLeft = (guideRect.left - originX) / rendered.width;
  const mirroredRight = (guideRect.right - originX) / rendered.width;
  const top = (guideRect.top - originY) / rendered.height;
  const bottom = (guideRect.bottom - originY) / rendered.height;

  // Un-mirror: the box's left edge on screen is its right edge in the raw frame.
  const rawLeft = clamp01(1 - mirroredRight) * videoW;
  const rawRight = clamp01(1 - mirroredLeft) * videoW;
  const rawTop = clamp01(top) * videoH;
  const rawBottom = clamp01(bottom) * videoH;

  const sw = rawRight - rawLeft;
  const sh = rawBottom - rawTop;
  // Fully off-frame (or a degenerate layout): nothing sensible to crop.
  if (sw < 1 || sh < 1) return null;
  return { sx: rawLeft, sy: rawTop, sw, sh };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

// Landmarks come back normalized to the CROP, so "in the box" reduces to "not hanging off
// the crop's edge": MediaPipe extrapolates landmarks past the image bounds for a partially
// visible face, which is exactly the case we must reject. Same center + 70%-area rule the
// full-frame check used, now expressed in crop space.
export function isFaceInsideCrop(landmarks: Array<{ x: number; y: number }> | undefined): boolean {
  if (!landmarks?.length) return false;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of landmarks) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const area = (maxX - minX) * (maxY - minY);
  if (!(area > 0)) return false;

  const insideArea =
    Math.max(0, Math.min(maxX, 1) - Math.max(minX, 0)) *
    Math.max(0, Math.min(maxY, 1) - Math.max(minY, 0));
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const centerInside = centerX >= 0 && centerX <= 1 && centerY >= 0 && centerY <= 1;
  return centerInside && insideArea / area >= FACE_GUIDE_MIN_INSIDE_RATIO;
}


async function createLandmarker(
  wasmUrl: string,
  modelUrl: string
): Promise<FaceLandmarker> {
  const vision = await FilesetResolver.forVisionTasks(wasmUrl);
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: modelUrl },
    runningMode: "VIDEO",
    numFaces: MAX_FACES,
    minFaceDetectionConfidence: FACE_MIN_CONFIDENCE,
    minFacePresenceConfidence: FACE_MIN_CONFIDENCE,
    minTrackingConfidence: FACE_MIN_CONFIDENCE,
    outputFacialTransformationMatrixes: true,
  });
}

// Deadlines for the two load attempts. Neither fetch is cancellable and a hung request
// (captive portal, a proxy that holds the socket open) never rejects on its own, so
// without these the load would stay pending, modelState would sit on "loading" and the
// capture button would be disabled for the life of the tab. Turning a stall into a
// rejection lets the existing chain fall back to the same-origin bundle and, if that
// stalls too, clear the cached promise so a later mount retries. The CDN gets the shorter
// budget: the local copy is same-origin and always shipped (prebuild runs
// scripts/copy-mediapipe-assets.js), so falling back early costs little.
// Exported so the timeout test advances by exactly this deadline instead of a copy of it.
export const CDN_LOAD_TIMEOUT_MS = 8000;
const LOCAL_LOAD_TIMEOUT_MS = 15000;

// Deadline for the <video> to report metadata for a stream getUserMedia already handed us.
// Generous: a USB webcam can take a second or two to negotiate a format. It exists only so
// a device that never reports at all cannot hang the screen forever.
export const VIDEO_METADATA_TIMEOUT_MS = 10000;

// Reject with `label` once `ms` has passed. The underlying load keeps running in the
// background — it cannot be aborted — and its result is simply ignored.
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

// Reuse a single landmarker across mounts to avoid re-loading the model. Load from the
// CDN first (per MORP-2078), then fall back to the same-origin copy bundled into
// public/mediapipe by scripts/copy-mediapipe-assets.js when the CDN is unreachable
// (offline / blocked) or too slow to answer within CDN_LOAD_TIMEOUT_MS. On total failure
// the cached rejection is cleared so a later mount can retry.
//
// Lifetime note: this instance is intentionally never close()d — it lives for the tab
// so remounting FaceCapture (register / re-register / check-in) is instant. All mounts
// share it; since only one FaceCapture is on screen at a time and detectForVideo needs
// monotonic timestamps, sharing is safe. If a future screen mounts two FaceCaptures at
// once, give each its own landmarker (per-instance ref) instead of this singleton.
let faceLandmarkerPromise: Promise<FaceLandmarker> | null = null;
function loadFaceLandmarker(): Promise<FaceLandmarker> {
  if (!faceLandmarkerPromise) {
    faceLandmarkerPromise = withTimeout(
      createLandmarker(CDN_WASM_URL, CDN_MODEL_URL),
      CDN_LOAD_TIMEOUT_MS,
      "[face-capture] CDN face model"
    )
      .catch((cdnErr) => {
        console.warn(
          "[face-capture] CDN load failed, falling back to local bundle:",
          cdnErr
        );
        return withTimeout(
          createLandmarker(LOCAL_WASM_URL, LOCAL_MODEL_URL),
          LOCAL_LOAD_TIMEOUT_MS,
          "[face-capture] local face model"
        );
      })
      .catch((err) => {
        faceLandmarkerPromise = null;
        throw err;
      });
  }
  return faceLandmarkerPromise;
}

// The Salesforce app's web view on an iPhone that cannot run the worker path (iOS 17 and
// older): falling back to the <video> would only bring back the blank fullscreen player.
const IN_APP_CAMERA_UNSUPPORTED = "InAppCameraUnsupported";

function inAppCameraUnsupported(cause: unknown): Error {
  const err = new Error(`worker frames unavailable in the Salesforce app web view: ${String(cause)}`);
  err.name = IN_APP_CAMERA_UNSUPPORTED;
  return err;
}

function cameraErrorMessage(err: any): string {
  const name = err?.name;
  if (name === IN_APP_CAMERA_UNSUPPORTED) return messages.face.cameraInAppUnsupported;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return messages.face.cameraPermissionDenied;
  }
  return messages.face.cameraError;
}

// Video tracks of a stream, never throwing. Everything below is diagnostics, and a
// diagnostic must never be able to break the camera: a MediaStream that does not implement
// getVideoTracks (an older polyfill, a test double) would otherwise turn a working camera
// into a failed start.
function videoTracksOf(stream: MediaStream | null): MediaStreamTrack[] {
  try {
    return stream?.getVideoTracks?.() ?? [];
  } catch {
    return [];
  }
}

// Wait until the <video> reports metadata for the stream just attached to it. Resolving is
// not the only possible outcome: if the device disappears while the camera is still
// starting (USB pulled between getUserMedia resolving and the first frame), the browser
// fires no "loadedmetadata" at all, so the track's own "ended" event is raced against it.
// Without that race the caller's await never settles, leaving the screen stuck on
// 「カメラを起動しています」 with its retry button disabled for the life of the tab — exactly
// the failure mode withTimeout already guards against for the model load.
function waitForVideoMetadata(video: HTMLVideoElement, stream: MediaStream): Promise<void> {
  if (video.readyState >= 1) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const tracks = videoTracksOf(stream).filter(
      (track) => typeof track.addEventListener === "function"
    );
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onMeta);
      for (const track of tracks) track.removeEventListener("ended", onEnded);
    };
    const onMeta = () => {
      cleanup();
      resolve();
    };
    const onEnded = () => {
      cleanup();
      reject(new Error("camera track ended before metadata"));
    };
    video.addEventListener("loadedmetadata", onMeta);
    for (const track of tracks) track.addEventListener("ended", onEnded);
  });
}

// Diagnostics for a failed getUserMedia. The DOMException name is the only thing that
// tells the cases apart (permission blocked vs no device vs device busy vs constraints),
// and it is exactly what is lost by the time the user reports "camera doesn't work" —
// so dump it together with the surrounding environment (secure origin, device list,
// Permissions API state). Async on purpose: enumerateDevices()/permissions.query() are
// promises, and this must never delay the error modal, so it is fire-and-forget.
async function logCameraFailure(err: any) {
  if (!DIAGNOSTICS_ENABLED) return;
  try {
    await dumpCameraFailure(err);
  } catch {
    // A diagnostic that throws would surface as an unhandled rejection (it is called
    // fire-and-forget) and tell the developer nothing useful. Swallow it.
  }
}

async function dumpCameraFailure(err: any) {
  const name = err?.name ?? "(no name)";
  // Groups match camera-error-cases.csv.
  const group =
    name === "NotAllowedError" || name === "SecurityError"
      ? "PERMISSION_DENIED (camera access blocked)"
      : name === "NotFoundError" ||
        name === "NotReadableError" ||
        name === "OverconstrainedError" ||
        name === "AbortError"
      ? "DEVICE_UNUSABLE (camera missing, busy or broken)"
      : "UNKNOWN";

  console.group("[face-capture] getUserMedia failed");
  console.log("error.name:", name);
  console.log("group:", group);
  console.log("error.message:", err?.message);
  // OverconstrainedError only: which constraint the device could not satisfy.
  if (err?.constraint) console.log("error.constraint:", err.constraint);
  console.log("constraints requested:", VIDEO_CONSTRAINTS);
  console.log("isSecureContext:", window.isSecureContext, "origin:", window.location.origin);
  console.log("mediaDevices available:", Boolean(navigator.mediaDevices?.getUserMedia));
  console.log("userAgent:", navigator.userAgent);
  console.log("raw error:", err);

  try {
    // Labels stay empty until permission is granted, so an all-blank list is itself a
    // signal that the camera was never authorised on this origin.
    const devices = await navigator.mediaDevices?.enumerateDevices();
    const cameras = (devices ?? []).filter((d) => d.kind === "videoinput");
    console.log(
      `videoinput devices: ${cameras.length}`,
      cameras.map((d) => ({ deviceId: d.deviceId, label: d.label || "(no label)" }))
    );
  } catch (listErr) {
    console.log("enumerateDevices failed:", listErr);
  }

  try {
    // Not implemented everywhere (Safari/Firefox); guarded so the log never throws.
    const status = await (navigator.permissions as any)?.query?.({ name: "camera" });
    console.log("permissions.camera:", status?.state ?? "(unsupported)");
  } catch (permErr) {
    console.log("permissions.query unsupported:", permErr);
  }
  console.groupEnd();
}

function CameraStartPromptCard() {
  const renderLines = (text: string) =>
    text.split("\n").map((line) => (
      <span key={line} className="camera-prompt-line">
        {line}
      </span>
    ));

  return (
    <span className="camera-prompt-card">
      <span className="camera-prompt-lines camera-prompt-lines--desktop">
        {renderLines(messages.face.startCameraDesktop)}
      </span>
      <span className="camera-prompt-lines camera-prompt-lines--mobile">
        {renderLines(messages.face.startCamera)}
      </span>
    </span>
  );
}


// Live-camera face capture, shared by registration and check-in. Keeps the same controlled
// API (value/onChange/disabled) for registration; check-in auto-submits via onCapture.
export const FaceCapture = forwardRef<FaceCaptureHandle, Props>(function FaceCapture(
  {
    value,
    onChange,
    onCapture,
    disabled,
    submitting,
    onCameraError,
    suppressCameraErrorDialog = false,
    mode = "register",
    loading = false,
    onUiStateChange,
  },
  ref
) {
  // Check-in auto-captures and submits immediately (no review step); registration keeps the
  // manual capture + preview + confirm flow.
  const isCheckin = mode === "checkin";
  // Strict for registration, lenient for check-in (MORP-2081).
  const centerPoint = isCheckin ? CENTER_POINT_CHECKIN : CENTER_POINT_REGISTER;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Hidden canvas used only for capture(): draws the frame WITHOUT mirroring so the
  // image sent to the backend matches reality (not the mirrored preview).
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Visible canvas that renders the mirrored (ctx.scale(-1,1)) live preview.
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const guideBoxRef = useRef<HTMLDivElement | null>(null);
  // Cached 2D context for the preview canvas so we don't re-fetch it every frame.
  // Re-acquired whenever the underlying canvas element changes (e.g. remount).
  const previewCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  // Offscreen canvas holding the guide-box crop that is fed to the detector, plus its
  // cached context. Created lazily and resized only when the crop geometry changes.
  const detectCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const detectCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  // The object URL currently backing the preview <img>. FaceCapture created it and must
  // revoke it when it is replaced or cleared, so captured blobs aren't leaked.
  const objectUrlRef = useRef<string | null>(null);
  // Bumped on every capture() and whenever the image is cleared. toBlob() is async, so its
  // callback checks this against the generation captured at call time and bails if stale —
  // guarding against a late callback (from a superseded manual/auto capture, or after a
  // retake) emitting an image the user has already moved past.
  const captureGenRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  // Where frames come from: the <video> (default), or a worker when a playing <video>
  // would be forced fullscreen (see camera-frame-worker.ts). Chosen per camera start.
  const frameModeRef = useRef<FrameSourceMode>("video");
  const workerSourceRef = useRef<WorkerFrameSource | null>(null);
  // Worker mode: the latest frame is drawn here and stands in for the <video> everywhere
  // (preview, detection, capture). The count doubles as the watchdog's progress signal.
  const workerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const workerCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const workerFrameCountRef = useRef(0);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const rafRef = useRef<number | null>(null);
  // Interval id for the frozen-preview watchdog (diagnostics only).
  const watchdogRef = useRef<number | null>(null);
  // Latest onChange / onCapture, read from the async toBlob callback so a capture that
  // started under a previous render still notifies the current parent.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  // Read from the track "ended" listener, which outlives the render that attached it.
  const onCameraErrorRef = useRef(onCameraError);
  onCameraErrorRef.current = onCameraError;
  // True once a mid-session camera loss has been reported, so several video tracks ending
  // together (or an "ended" racing the startCamera catch) report it only once. Cleared by
  // startCamera so a retry can report a fresh failure.
  const cameraLostRef = useRef(false);
  // Same pattern for the center-point threshold, so a mode change is picked up by the loop.
  const centerPointRef = useRef(centerPoint);
  centerPointRef.current = centerPoint;
  // Previous COMMITTED isCentered decision (state would be stale inside the rAF closure).
  // Committed only after the candidate holds for a few reads (see below).
  const poseFrontalRef = useRef<boolean | null>(null);
  // Debounce for the centered flag: a candidate must repeat for STABLE_READS before it is
  // committed. Absorbs transient detection dropouts (a stray 0-face frame while tilted)
  // and boundary noise, so the hint stops flickering.
  const stablePoseRef = useRef<{ value: boolean | null; streak: number }>({
    value: null,
    streak: 0,
  });
  // Debounce for "a single face is present but the platform gives no usable pose matrix"
  // (e.g. a build/model without facial transformation matrixes). Only once this has held
  // for STABLE_READS do we soft-degrade and allow capture — a transient matrix-less frame
  // must NOT open the gate by default (that's the whole point of gating on a frontal pose).
  const noMatrixRef = useRef<{ value: boolean; streak: number }>({
    value: false,
    streak: 0,
  });
  const lastDetectAtRef = useRef(0);
  const stableCountRef = useRef<{ value: number | null; streak: number }>({
    value: null,
    streak: 0,
  });
  const stableInGuideRef = useRef<{ value: boolean | null; streak: number }>({
    value: null,
    streak: 0,
  });
  const autoOpenedRef = useRef(false);
  // Latest cameraState, read from the `value` effect (which must not re-run on state
  // changes) to tell "the camera was running before the capture" from "never started".
  const cameraStateRef = useRef<CameraState>("idle");
  // Was the live camera running when the photo was taken? Set on capture, consumed when the
  // image is cleared to decide whether to reopen the camera automatically on retake.
  const wasLiveRef = useRef(false);
  const [cameraState, setCameraState] = useState<CameraState>("idle");
  cameraStateRef.current = cameraState;
  const [error, setError] = useState("");
  // Blocking failures shown in the エラー modal instead of the inline banner: the camera
  // could not be opened (cameraError / cameraPermissionDenied) and the face model failed
  // to load (modelUnavailable). Everything the user can act on right away (cameraNotReady,
  // face-guide hints) stays inline so it doesn't interrupt them mid-capture.
  const [dialogError, setDialogError] = useState("");
  // The model failure is announced once per camera start (the load is attempted from
  // attachStream only), and startCamera clears it — so a user who explicitly retries is
  // told again instead of facing a silently disabled capture button, while the state
  // itself never re-fires the modal on its own.
  const modelDialogShownRef = useRef(false);
  const [faceCount, setFaceCount] = useState<number | null>(null);
  const [modelState, setModelState] = useState<
    "idle" | "loading" | "ready" | "unavailable"
  >("idle");
  // Whether the single detected face is frontal enough to capture. null = unknown / not
  // yet committed (initial debounce, or no usable pose matrix). Capture is gated on an
  // explicit `true`; null keeps the gate CLOSED (see captureReady) so a mid-tilt / pending
  // pose can't leak through.
  const [poseFrontal, setPoseFrontal] = useState<boolean | null>(null);
  // Soft-degrade flag: the platform consistently returns no pose matrix for a present
  // face, so the frontal gate can never commit. Once this holds (see noMatrixRef) we
  // allow capture rather than trapping the user behind a check the platform can't answer.
  const [poseUnavailable, setPoseUnavailable] = useState(false);
  // The detected single face must be inside the on-screen guide frame.
  // null = not stable yet (or geometry not ready), so the gate stays closed.
  const [faceInGuide, setFaceInGuide] = useState<boolean | null>(null);

  // Capture is allowed only for exactly one, confirmed-frontal face. The gate stays CLOSED
  // while the pose is still unknown (poseFrontal === null: initial debounce, mid-tilt, or a
  // missing matrix) so a non-frontal frame can't slip through. The sole exception is
  // poseUnavailable — a sustained "single face but no pose matrix" streak soft-degrades to
  // allowing capture rather than trapping the user behind a check the platform can't answer.
  // faceCount already counts ONLY faces inside the guide frame (see the detect loop), so a
  // face off to the side can never reach this gate — no separate faceInGuide clause needed.
  const captureReady =
    faceCount === 1 && (poseFrontal === true || poseUnavailable);

  useEffect(() => {
    onUiStateChange?.({
      cameraState,
      captureReady,
      hasImage: Boolean(value),
      modelLoading: modelState === "loading",
    });
  }, [cameraState, captureReady, value, modelState, onUiStateChange]);

  // Diagnostics: the browser fires devicechange the moment a camera is plugged in or
  // pulled out, whether or not the current stream reacts — so this is the one signal that
  // is guaranteed to appear when the user physically unplugs the webcam.
  useEffect(() => {
    if (!DIAGNOSTICS_ENABLED) return;
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const onDeviceChange = async () => {
      try {
        const devices = await md.enumerateDevices();
        const cameras = devices.filter((d) => d.kind === "videoinput");
        console.warn("[face-capture] devicechange — videoinput count:", cameras.length, {
          cameras: cameras.map((d) => d.label || "(no label)"),
          currentStreamActive: streamRef.current?.active ?? false,
          currentTrackState: videoTracksOf(streamRef.current)[0]?.readyState ?? "(none)",
        });
      } catch (err) {
        console.warn("[face-capture] devicechange — enumerateDevices failed:", err);
      }
    };
    md.addEventListener("devicechange", onDeviceChange);
    console.log("[face-capture] devicechange listener attached");
    return () => md.removeEventListener("devicechange", onDeviceChange);
  }, []);

  // Clean up the camera and any live preview URL on unmount.
  useEffect(() => {
    return () => {
      stopCamera();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registration preview lifecycle. Once an image exists there's no need to keep the
  // stream; when the parent clears it ("retake") the camera is reopened here. Check-in
  // never sets `value`, so this stays inert there.
  useEffect(() => {
    if (value) {
      // Remember whether the camera was actually live, then drop BOTH the stream and the
      // "ready" flag. Leaving cameraState === "ready" behind a torn-down stream is what
      // produced the grey screen: the live view re-rendered with a blank canvas, no draw
      // loop, and no "tap to start" overlay (that overlay is gated on !== "ready").
      wasLiveRef.current = cameraStateRef.current === "ready";
      stopCamera();
      setCameraState("idle");
    } else {
      // Image cleared (retake / after submit): invalidate any in-flight capture so a late
      // toBlob callback can't re-populate the preview, then drop its object URL.
      captureGenRef.current += 1;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (wasLiveRef.current) {
        // Retake is driven from OUTSIDE (the parent's 「撮り直す」 just calls onChange(null)),
        // so no stream was pre-acquired in a click handler. Reopen it — permission is already
        // granted at this point. If this fails, cameraState lands on "error"/"idle", both of
        // which render a tappable overlay, so the user is never stuck.
        wasLiveRef.current = false;
        startCamera();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Auto-capture for check-in only: fire capture() once the gate (single, centered, frontal
  // face) has held continuously for AUTO_CAPTURE_MS. The captured frame is handed straight
  // to the parent for immediate verification — there is no review/confirm step. Re-running
  // whenever captureReady flips means a gate drop (2+ faces, no face, off-center) clears the
  // timer and restarts the count. `disabled` (terminal lock) and `submitting` (verify in
  // flight) both pause it, so we never fire again mid-request or after a terminal success;
  // when a retryable verify ends, `submitting` flips back to false and this re-arms a fresh
  // 2s timer while the face is still framed.
  useEffect(() => {
    if (!isCheckin || disabled || submitting || cameraState !== "ready" || !captureReady) {
      return;
    }
    const id = window.setTimeout(() => capture(), AUTO_CAPTURE_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckin, disabled, submitting, cameraState, captureReady]);


  useEffect(() => {
    if (!isCheckin || disabled || cameraState !== "idle") {
      return;
    }
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckin, disabled, cameraState]);

  // The camera died after it was already running (USB unplugged, another app seized it, the
  // permission revoked from the omnibox). getUserMedia never rejects for this, so without
  // this handler the preview would simply freeze: no error, no retry affordance, and on
  // check-in no secret-question link — the user would be stuck until they reload.
  function handleCameraLost() {
    if (cameraLostRef.current) return;
    cameraLostRef.current = true;
    // Drop the dead stream so the render loop stops and a retry starts from scratch.
    stopCamera();
    setCameraState("error");
    // The device is gone rather than blocked, so this is the hardware message, never the
    // permission one.
    const msg = messages.face.cameraError;
    if (!suppressCameraErrorDialog) setDialogError(msg);
    onCameraErrorRef.current?.(msg);
  }

  async function startCamera() {
    setError("");
    cameraLostRef.current = false;
    // A previous failure is stale the moment they retry — close the modal with it, and
    // re-arm the model announcement for the load this start will trigger.
    setDialogError("");
    modelDialogShownRef.current = false;
    setCameraState("starting");
    let stream: MediaStream | null = null;
    try {
      // No mediaDevices at all (insecure origin / unsupported browser): fail with the
      // camera error instead of a TypeError, so the エラー modal explains it.
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("getUserMedia unavailable");
      }
      // getUserMedia must run inside the user gesture that called startCamera so
      // iOS/Safari grants it; attachStream then wires the stream to the <video>.
      stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      // Success side: the settings the browser actually negotiated (they can differ from
      // VIDEO_CONSTRAINTS' ideal values), so a "camera opens but looks wrong" report can
      // be told apart from the failures logged below.
      if (DIAGNOSTICS_ENABLED) {
        const openedTrack = videoTracksOf(streamRef.current)[0];
        console.log("[face-capture] camera opened:", {
          label: openedTrack?.label,
          settings: openedTrack?.getSettings?.(),
        });
      }
      await attachStream();
    } catch (err: any) {
      // Superseded: the camera was stopped (lost, retake, retry) while this start was in
      // flight. Whoever stopped it owns the outcome, and stopCamera() here would tear down
      // a newer session's stream.
      if (stream && streamRef.current !== stream) return;
      void logCameraFailure(err);
      // getUserMedia may already have handed us a live stream before the failure (metadata
      // timeout, play() rejected). Release it, or the camera light stays on and the device
      // stays busy for a stream nothing will ever read.
      stopCamera();
      setCameraState("error");
      // The track ending mid-startup rejects the wait above AND fires handleCameraLost.
      // It already reported the failure, so stop here rather than firing onCameraError a
      // second time for one physical event.
      if (cameraLostRef.current) return;
      const msg = cameraErrorMessage(err);
      // Modal, not a banner: nothing on the screen works until the camera opens, and the
      // "tap to start" overlay behind it is the retry once the modal is dismissed. The
      // exception is a parent that offers a way out of its own (check-in's secret-question
      // link) — it renders the message itself, so a modal would only sit on top of it.
      if (!suppressCameraErrorDialog) setDialogError(msg);
      onCameraError?.(msg);
    }
  }

  // Attach an already-acquired stream (streamRef.current) to the <video> and start the
  // render/detection loops. Split out from startCamera so "retake" can acquire the
  // stream inside its click handler (preserving the iOS user gesture) and defer the
  // attach until the value effect has remounted the <video>.
  async function attachStream() {
    const stream = streamRef.current;
    if (!stream) return;
    // The camera dying AFTER it opened (USB webcam unplugged, another app seizing it, the
    // user revoking permission from the omnibox) produces no getUserMedia rejection, so it
    // has to be caught from the track itself. "ended" alone is NOT enough: browsers fire it
    // only for some losses and otherwise just stop delivering frames on a still-"live"
    // track, so "mute" and the watchdog below cover the rest.
    for (const track of videoTracksOf(stream)) {
      if (typeof track.addEventListener !== "function") continue;
      const trackInfo = () => ({
        label: track.label,
        readyState: track.readyState,
        muted: track.muted,
      });
      // "ended" is the one signal dependable enough to act on: per spec our own
      // track.stop() does NOT fire it, so every "ended" really is the device going away
      // rather than us closing the camera.
      track.addEventListener("ended", () => {
        if (DIAGNOSTICS_ENABLED) {
          console.warn("[face-capture] video track ENDED (camera lost):", trackInfo());
        }
        handleCameraLost();
      });
      // "mute" stays diagnostics-only on purpose: it also fires for transient stalls (tab
      // hidden, screen locked, device busy for a moment) and is followed by "unmute", so
      // acting on it would interrupt a user who is mid-scan.
      if (DIAGNOSTICS_ENABLED) {
        track.addEventListener("mute", () => {
          console.warn(
            "[face-capture] video track MUTED (no frames — device busy/unplugged):",
            trackInfo()
          );
        });
        track.addEventListener("unmute", () => {
          console.log("[face-capture] video track unmuted (frames resumed):", trackInfo());
        });
        console.log("[face-capture] track listeners attached:", trackInfo());
      }
    }
    // Every await below can outlive this camera session (stopped, then restarted), so each
    // one is followed by this check: a superseded start must not touch the shared refs.
    const superseded = () => streamRef.current !== stream;
    frameModeRef.current = preferredFrameSourceMode();
    if (frameModeRef.current === "worker") {
      try {
        await attachWorkerFrames(stream);
      } catch (err) {
        if (superseded()) return;
        // The camera itself is gone: the <video> would only wait out its own timeout.
        if (cameraLostRef.current) throw err;
        // In the Salesforce app the <video> would be forced fullscreen again, so tell the
        // user what to do instead of leaving them on a blank screen.
        if (isSalesforceIPhoneWebView()) throw inAppCameraUnsupported(err);
        // Forced worker mode elsewhere (?faceFrameSource=worker): fall back to the <video>.
        console.warn("[face-capture] worker frames unavailable, using <video>:", err);
        frameModeRef.current = "video";
      }
      if (superseded()) return;
    }
    const video = videoRef.current;
    if (frameModeRef.current === "video" && video) {
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.srcObject = stream;
      // Both guards feed the caller's catch, which already turns any throw here into the
      // camera-error state (and the secret-question link on check-in).
      await withTimeout(
        waitForVideoMetadata(video, stream),
        VIDEO_METADATA_TIMEOUT_MS,
        "[face-capture] camera metadata"
      );
      if (superseded()) return;
      await video.play();
      if (superseded()) return;
    }
    if (DIAGNOSTICS_ENABLED) {
      console.log("[face-capture] frame source:", frameModeRef.current);
    }
    setCameraState("ready");
    // Preview render loop runs independently of the model; face detection is folded
    // into the same loop and only kicks in once the landmarker has loaded.
    startFrameLoop();
    startCameraWatchdog();
    startFaceDetection();
  }

  // Worker mode: read the camera track off-thread and resolve once the first frame has been
  // drawn, under the same deadline the <video> gets for its metadata.
  async function attachWorkerFrames(stream: MediaStream) {
    const track = videoTracksOf(stream)[0];
    if (!track) throw new Error("no video track to read");
    workerFrameCountRef.current = 0;
    const source = startWorkerFrameSource(track, {
      onFrame: drawWorkerFrame,
      onEnded: handleCameraLost,
      onError: (message) => {
        console.warn("[face-capture] worker frames stopped:", message);
        handleCameraLost();
      },
    });
    workerSourceRef.current = source;
    try {
      await withTimeout(
        source.firstFrame,
        VIDEO_METADATA_TIMEOUT_MS,
        "[face-capture] worker first frame"
      );
    } catch (err) {
      // Stop this attempt's own source: by now the ref may hold a newer session's.
      source.stop();
      if (workerSourceRef.current === source) workerSourceRef.current = null;
      throw err;
    }
  }

  // Draw the worker's raw bitmap upright — the same picture a <video> would render — so
  // preview, detection and the captured image all see the face the right way up.
  function drawWorkerFrame(bitmap: ImageBitmap, orientation: FrameOrientation) {
    try {
      let canvas = workerCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement("canvas");
        workerCanvasRef.current = canvas;
        workerCtxRef.current = null;
      }
      const size = orientedSize(bitmap.width, bitmap.height, orientation.rotation);
      if (canvas.width !== size.width || canvas.height !== size.height) {
        canvas.width = size.width;
        canvas.height = size.height;
        workerCtxRef.current = null;
      }
      let ctx = workerCtxRef.current;
      if (!ctx) {
        ctx = canvas.getContext("2d");
        workerCtxRef.current = ctx;
      }
      if (ctx) drawOrientedFrame(ctx, bitmap, orientation);
      workerFrameCountRef.current += 1;
    } finally {
      bitmap.close();
    }
  }

  function stopWorkerFrames() {
    workerSourceRef.current?.stop();
    workerSourceRef.current = null;
    workerFrameCountRef.current = 0;
    // Don't keep the last face frame around after the camera is closed.
    const canvas = workerCanvasRef.current;
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
      workerCtxRef.current = null;
    }
  }

  // The current frame, or null while the active source has nothing drawable yet.
  function currentFrame(): Frame | null {
    if (frameModeRef.current === "worker") {
      const canvas = workerCanvasRef.current;
      if (!canvas || !workerFrameCountRef.current || !canvas.width || !canvas.height) {
        return null;
      }
      return { image: canvas, size: { videoWidth: canvas.width, videoHeight: canvas.height } };
    }
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      return null;
    }
    return { image: video, size: video };
  }

  async function startFaceDetection() {
    // Reuse an already-loaded landmarker without flashing the loading state again.
    if (!landmarkerRef.current) setModelState("loading");
    try {
      const landmarker = await loadFaceLandmarker();
      // The camera may have been stopped while the model was loading.
      if (!streamRef.current) {
        setModelState("idle");
        return;
      }
      // The frame loop (started in startCamera) picks up the landmarker from this ref;
      // no separate detection loop is spun up here.
      landmarkerRef.current = landmarker;
      setModelState("ready");
    } catch {
      landmarkerRef.current = null;
      setModelState("unavailable");
      // Detection soft-degrades (capture stays available), so this is announced once and
      // the status bar keeps carrying the state after the modal is closed.
      if (!modelDialogShownRef.current) {
        modelDialogShownRef.current = true;
        setDialogError(messages.face.modelUnavailable);
      }
    }
  }

  // Draw the current video frame onto the visible preview canvas, mirrored horizontally
  // via ctx.scale(-1,1) so the user sees themselves as in a mirror. This only affects
  // the preview — capture() draws from the un-mirrored video, so the backend image is
  // never flipped.
  function drawMirroredPreview(frame: Frame) {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const { videoWidth, videoHeight } = frame.size;
    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
    }
    let ctx = previewCtxRef.current;
    if (!ctx || ctx.canvas !== canvas) {
      ctx = canvas.getContext("2d");
      previewCtxRef.current = ctx;
    }
    if (!ctx) return;
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(frame.image, -canvas.width, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  // Draw the guide-box region of the current frame into the offscreen detect canvas and
  // return it, so inference runs on those pixels alone. Returns null when the guide isn't
  // measurable yet (camera starting, layout not settled) — the caller then falls back to the
  // full frame. The crop is NOT mirrored: the detector (and the head-pose matrix it yields)
  // works in raw video orientation, same as before.
  function guideCropSource(frame: Frame): HTMLCanvasElement | null {
    const previewCanvas = previewCanvasRef.current;
    const guideBox = guideBoxRef.current;
    if (!previewCanvas || !guideBox) return null;
    const crop = getGuideCropInVideo(frame.size, previewCanvas, guideBox);
    if (!crop) return null;

    let canvas = detectCanvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      detectCanvasRef.current = canvas;
      detectCtxRef.current = null;
    }
    const width = Math.round(crop.sw);
    const height = Math.round(crop.sh);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      detectCtxRef.current = null;
    }
    let ctx = detectCtxRef.current;
    if (!ctx) {
      // No willReadFrequently: the pixels are read back by MediaPipe (GPU upload), not by us.
      ctx = canvas.getContext("2d");
      detectCtxRef.current = ctx;
    }
    if (!ctx) return null;
    ctx.drawImage(frame.image, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, width, height);
    return canvas;
  }

  // Single requestAnimationFrame loop: renders the mirrored preview every frame and,
  // once the landmarker is loaded, runs throttled face detection off the SAME (raw,
  // un-mirrored) frame.
  function startFrameLoop() {
    // Cancel any loop already scheduled so a re-entry (e.g. camera restarted before
    // the previous stop) can't run two loops in parallel.
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const tick = () => {
      // Stop looping once the camera is torn down or the stream is gone.
      if (!streamRef.current) {
        rafRef.current = null;
        return;
      }
      const frame = currentFrame();
      if (frame) {
        drawMirroredPreview(frame);

        const landmarker = landmarkerRef.current;
        const now = performance.now();
        if (landmarker && now - lastDetectAtRef.current >= DETECT_INTERVAL_MS) {
          lastDetectAtRef.current = now;
          try {
            // Detection input: the guide box region only. Everything outside it is cropped
            // away BEFORE inference, so a face off to the side (or a bystander) is never
            // processed at all — the model only ever sees what the user framed. When the
            // guide isn't on screen yet the whole frame is used as a fallback.
            const cropSource = guideCropSource(frame);
            const source = cropSource ?? frame.image;

            // One inference per (throttled) frame drives BOTH the face-count gate and
            // the head-pose read — the performance optimization is not detecting twice.
            const result = landmarker.detectForVideo(source, now);
            const count = result.faceLandmarks.length;

            // A face can still hang off the edge of the crop (half in the box): landmarks are
            // normalized to the crop, so that shows up as a bounding box spilling past [0,1].
            // Reject those — they are the "move your face into the frame" case. Evaluated per
            // face so "one face framed + one at the edge" still reads as one.
            const cropped = Boolean(cropSource);
            const guideFlags = cropped
              ? result.faceLandmarks.map((landmarks) => isFaceInsideCrop(landmarks))
              : null;
            const inGuideCount = guideFlags ? guideFlags.filter(Boolean).length : 0;
            // The face the pose is read from: the first one properly inside the guide.
            const primaryIndex = guideFlags ? Math.max(0, guideFlags.indexOf(true)) : 0;
            // Tri-state hint: true = a face is framed, false = a face reaches into the box but
            // isn't framed yet (→ "move into the frame"), null = nothing detected at all.
            const inGuide = !cropped || count === 0 ? null : inGuideCount > 0;

            const countForGate = cropped ? inGuideCount : count;
            const poseUsable = countForGate === 1;

            // Head pose of the primary face, derived from its 4x4 transformation matrix.
            // Only meaningful for a single in-guide face.
            const matrix = poseUsable
              ? result.facialTransformationMatrixes?.[primaryIndex]?.data
              : null;
            const pose = headPoseFromMatrix(matrix);

            // Soft-degrade streak: a single face is present but yields no usable pose.
            // Committed to poseUnavailable only after STABLE_READS so a transient
            // matrix-less frame never opens the gate on its own.
            const noMatrix = poseUsable && pose === null;
            const nm = noMatrixRef.current;
            if (noMatrix === nm.value) {
              nm.streak += 1;
            } else {
              nm.value = noMatrix;
              nm.streak = 1;
            }
            if (nm.streak >= STABLE_READS) {
              setPoseUnavailable((prev) => (prev === noMatrix ? prev : noMatrix));
            }

            // Center-point gate: nextCentered gives the instantaneous isCentered candidate
            // for the active mode's threshold; a streak debounce then requires it to persist
            // for STABLE_READS reads before committing. The debounce stops the hint from
            // flickering on transient dropouts and boundary noise while the head is tilted.
            const candidate = nextCentered(pose, centerPointRef.current);
            const stablePose = stablePoseRef.current;
            if (candidate === stablePose.value) {
              stablePose.streak += 1;
            } else {
              stablePose.value = candidate;
              stablePose.streak = 1;
            }
            if (stablePose.streak >= STABLE_READS && poseFrontalRef.current !== candidate) {
              poseFrontalRef.current = candidate;
              setPoseFrontal(candidate);
            }
            const stableInGuide = stableInGuideRef.current;
            if (inGuide === stableInGuide.value) {
              stableInGuide.streak += 1;
            } else {
              stableInGuide.value = inGuide;
              stableInGuide.streak = 1;
            }
            if (stableInGuide.streak >= STABLE_READS) {
              setFaceInGuide((prev) => (prev === inGuide ? prev : inGuide));
            }

            const stable = stableCountRef.current;
            if (countForGate === stable.value) {
              stable.streak += 1;
            } else {
              stable.value = countForGate;
              stable.streak = 1;
            }
            // Commit only once the same count has held for a few reads. React bails
            // out when the value is unchanged, so this won't cause extra renders.
            if (stable.streak >= STABLE_READS) {
              setFaceCount((prev) => (prev === countForGate ? prev : countForGate));
            }
          } catch {
            // Ignore transient detection errors (e.g. video briefly not ready).
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  // Diagnostics watchdog: the most common "camera broke" symptom is a preview that simply
  // stops advancing while the track still reports itself live, which fires no event at all.
  // Polling video.currentTime is the only way to see it. Logs once per freeze (and once
  // when frames come back) so a long freeze doesn't flood the console.
  function startCameraWatchdog() {
    stopCameraWatchdog();
    if (!DIAGNOSTICS_ENABLED) return;
    let lastTime = -1;
    let frozenTicks = 0;
    let reported = false;
    watchdogRef.current = window.setInterval(() => {
      const video = videoRef.current;
      const track = videoTracksOf(streamRef.current)[0];
      if (!video || !track) return;
      const progress =
        frameModeRef.current === "worker" ? workerFrameCountRef.current : video.currentTime;
      const advanced = progress !== lastTime;
      lastTime = progress;
      if (advanced) {
        if (reported) {
          console.log("[face-capture] preview recovered, frames advancing again");
        }
        frozenTicks = 0;
        reported = false;
        return;
      }
      frozenTicks += 1;
      // Two quiet seconds: past any normal decode hiccup, so this is a real stall.
      if (frozenTicks >= 2 && !reported) {
        reported = true;
        console.warn("[face-capture] preview FROZEN — no new frames:", {
          trackReadyState: track.readyState,
          trackMuted: track.muted,
          trackEnabled: track.enabled,
          streamActive: streamRef.current?.active,
          videoReadyState: video.readyState,
          videoCurrentTime: video.currentTime,
        });
      }
    }, 1000);
  }

  function stopCameraWatchdog() {
    if (watchdogRef.current !== null) {
      window.clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  }

  function stopFrameLoop() {
    stopCameraWatchdog();
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastDetectAtRef.current = 0;
    stableCountRef.current = { value: null, streak: 0 };
    setFaceCount(null);
    poseFrontalRef.current = null;
    stablePoseRef.current = { value: null, streak: 0 };
    setPoseFrontal(null);
    stableInGuideRef.current = { value: null, streak: 0 };
    setFaceInGuide(null);
    noMatrixRef.current = { value: false, streak: 0 };
    setPoseUnavailable(false);
  }

  function stopCamera() {
    stopFrameLoop();
    stopWorkerFrames();
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  }

  // Hand a captured blob up to the parent, wrapped with a fresh object URL. Revokes the
  // previous URL first so blobs aren't leaked. Check-in submits immediately via onCapture;
  // registration stores it as `value` (onChange) for the preview + confirm step.
  function emitImage(blob: Blob) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;
    const image = { blob, url };
    if (isCheckin) onCaptureRef.current?.(image);
    else onChangeRef.current?.(image);
  }

  function capture() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const frame = currentFrame();
    if (!frame) {
      setError(messages.face.cameraNotReady);
      return;
    }
    // Claim this capture's generation up front; a manual tap and the auto-capture timer can
    // both land here, and only the latest may commit its (async) blob.
    const gen = ++captureGenRef.current;
    canvas.width = frame.size.videoWidth;
    canvas.height = frame.size.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Draw with the default identity transform (NO mirroring). The preview is flipped
    // for UX only; the captured image must match reality for backend face matching.
    ctx.drawImage(frame.image, 0, 0, canvas.width, canvas.height);
    // toBlob() hands the backend a real binary blob (no base64 round-trip). It's async;
    // a null blob means the browser couldn't encode the frame, so surface a retry hint.
    canvas.toBlob(
      (blob) => {
        // Superseded by a newer capture or a retake while encoding — drop this result.
        if (gen !== captureGenRef.current) return;
        if (!blob) {
          setError(messages.face.cameraNotReady);
          return;
        }
        emitImage(blob);
      },
      "image/jpeg",
      0.92
    );
  }

  useImperativeHandle(ref, () => ({
    capture,
  }));

  // Camera/model failures, shown over whichever branch below is rendering. Closing it
  // leaves the screen in its retry state (the "tap to start" overlay, or the status bar
  // saying detection is off) rather than dead-ending the user.
  const errorDialog = (
    <ErrorDialog
      open={Boolean(dialogError)}
      message={dialogError}
      onClose={() => setDialogError("")}
    />
  );

  // Registration: image captured -> show preview + retake.
  if (value) {
    return (
      <div className="camera-card face-review">
        <p className="face-review-title">{messages.face.confirmRegister}</p>
        <div className={`face-review-panel ${loading ? "is-loading" : ""}`}>
          {/* Always mirrored: the frame comes from the live (mirrored) preview, so the
              review must match what the user saw. The stored blob stays un-mirrored. */}
          <img
            className="face-review-image is-mirrored"
            src={value.url}
            alt="captured face"
          />
          {loading ? (
            <div className="face-review-loading" role="status" aria-live="polite">
              <span className="face-register-spinner" aria-hidden="true">
                {Array.from({ length: 8 }).map((_, i) => (
                  <span
                    key={i}
                    style={{ transform: `rotate(${i * 45}deg)`, opacity: (i + 1) / 8 }}
                  />
                ))}
              </span>
              <span className="face-register-overlay-label">{messages.face.registering}</span>
            </div>
          ) : null}
        </div>
        {error ? <p className="error-banner face-registration-error">{error}</p> : null}
        {errorDialog}
      </div>
    );
  }

  // Guide-box color reflects the capture gate: green when ready, red for none / multiple
  // / tilted. Neutral while the model is still loading or before the first read.
  const guideStatus =
    modelState === "ready" && faceCount !== null
      ? captureReady
        ? "is-ok"
        : "is-warn"
      : "";

  // Live status feedback (model loading + face-guide messages), rendered as a full-width
  // bar above the frame (Figma).
  // "unavailable" is deliberately absent: that failure is reported by the エラー modal, so
  // repeating it here would say the same thing twice on one screen.
  const showFaceStatus =
    cameraState === "ready" && (modelState === "loading" || modelState === "ready");
  const faceStatusLoading = modelState === "loading";
  const faceStatusTone = faceStatusLoading ? "is-loading" : captureReady ? "is-ok" : "is-warn";
  const faceStatusText = faceStatusLoading
    ? messages.face.modelLoading
    : faceCount === null
    ? messages.face.detecting
    : faceCount === 0
    ? // Check-in drops out-of-frame faces from the count, so "0 faces" can mean either
      // nothing detected at all or a face detected outside the guide — tell them apart.
      faceInGuide === false
      ? messages.face.faceGuideFrame
      : // No face at all: the user is told what to DO (face the camera) rather than what
        // the detector failed to find, so this reuses faceGuidePose instead of a negative
        // "no face found" wording.
        messages.face.faceGuidePose
    : faceCount === 1
    ? captureReady
      ? messages.face.faceGuideSingle
      : messages.face.faceGuidePose
    : messages.face.multipleFaces;

  // Glyph in front of the status text: the green ring (perfectly.svg, Figma) once the face
  // passes the capture gate, otherwise the spinner. Every non-ok state — the model still
  // loading and each guide message (no face, off-centre, several faces) — is a state the
  // screen is still working out of, so the glyph keeps spinning rather than sitting as a
  // static dot.
  const faceStatusGlyph =
    faceStatusTone === "is-ok" ? (
      <Icon name="perfectly" size={12} className="face-status-icon" />
    ) : (
      <span className="face-status-spinner" aria-hidden="true" />
    );

  return (
    <div className="camera-card">
      {/* Status bar above the camera, tinted by state (green ready, red not). */}
      {showFaceStatus ? (
        <p className={`face-status face-status-bar ${faceStatusTone}`}>
          {faceStatusGlyph}
          {faceStatusText}
        </p>
      ) : null}
      <div className={`camera-frame ${cameraState === "ready" ? "is-ready" : ""}`}>
        {/* Raw video source: kept playing (decodes frames for the canvas + detector)
            but visually hidden behind the mirrored preview canvas. */}
        <video className="camera-video-source" ref={videoRef} autoPlay muted playsInline />
        <canvas
          className="camera-preview-canvas"
          ref={previewCanvasRef}
          aria-hidden="true"
        />
        {/* Face-positioning guide only makes sense once the live camera is running;
            before that (idle "tap to start" prompt) it must not show — matches Figma. */}
        {cameraState === "ready" ? (
          <div className="camera-guide" aria-hidden="true">
            <div ref={guideBoxRef} className={`camera-guide-box ${guideStatus}`} />
          </div>
        ) : null}
        {cameraState !== "ready" ? (
          <button
            className="camera-overlay-button is-register-prompt"
            type="button"
            disabled={cameraState === "starting" || disabled}
            onClick={startCamera}
          >
            {cameraState === "starting" ? (
              messages.face.startingCamera
            ) : (
              <CameraStartPromptCard />
            )}
          </button>
        ) : null}
        {/* Check-in only: processing overlay while a verify is in flight. The
            auto-capture timer is paused during this window, so it never double-fires. */}
        {isCheckin && submitting ? (
          <div className="camera-processing-overlay" aria-live="polite">
            <span className="camera-processing-spinner" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span
                  key={i}
                  style={{ transform: `rotate(${i * 45}deg)`, opacity: (i + 1) / 8 }}
                />
              ))}
            </span>
            <span className="camera-processing-label">{messages.checkin.submitting}</span>
          </div>
        ) : null}
      </div>

      <canvas hidden ref={canvasRef} />

      {error ? <p className="error-banner">{error}</p> : null}

      {errorDialog}
    </div>
  );
});
